import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Users, Calendar, Flag, ArrowLeft, ShieldAlert, FileText, ExternalLink, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatGBP, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TrustBadge from "../components/TrustBadge";
import Seo from "../components/Seo";

const PRESET_AMOUNTS = [10, 25, 50, 100, 250];

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(25);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");

  const fetchOne = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Campaign not found");
      navigate("/campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOne(); /* eslint-disable-next-line */ }, [id]);

  const handleDonate = async () => {
    if (!amount || amount < 1) {
      toast.error("Please enter an amount of at least £1");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/donations/checkout", {
        campaign_id: id,
        amount: parseFloat(amount),
        donor_name: donorName.trim() || undefined,
        donor_email: donorEmail.trim() || undefined,
        message: message.trim() || undefined,
        anonymous,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(formatErr(e));
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (reportReason.trim().length < 8) {
      toast.error("Please give a brief reason (at least 8 characters).");
      return;
    }
    try {
      await api.post("/reports", { campaign_id: id, reason: reportReason.trim() });
      toast.success("Thank you. Our team will review this campaign.");
      setShowReport(false);
      setReportReason("");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-brand-muted">Loading campaign…</div>;
  }
  if (!data) return null;

  const c = data.campaign;
  const pct = Math.min(100, Math.round(((c.amount_raised || 0) / Math.max(1, c.amount_needed)) * 100));
  const created = new Date(c.created_at).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
      <Seo
        title={c.title}
        description={(c.story || "").slice(0, 240)}
        image={c.cover_image_url}
        type="article"
      />
      <Link to="/campaigns" className="inline-flex items-center text-sm text-brand-muted hover:text-brand-primary transition-colors mb-6">
        <ArrowLeft size={15} strokeWidth={1.6} className="mr-1.5" /> Back to campaigns
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left: Story */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2">
          {c.cover_image_url && (
            <div className="rounded-3xl overflow-hidden mb-8">
              <img src={c.cover_image_url} alt={c.title} className="w-full h-72 sm:h-[420px] object-cover" data-testid="campaign-cover" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="eyebrow">{c.category}</span>
            <span className="text-brand-border">•</span>
            <span className="text-xs uppercase tracking-widest text-brand-muted font-semibold">{c.urgency}</span>
            <TrustBadge level={c.creator_verification} />
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl text-brand-text leading-tight" data-testid="campaign-title">{c.title}</h1>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm text-brand-muted">
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.6} />{c.location}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.6} />Created {created}</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14} strokeWidth={1.6} />{c.donor_count || 0} donors</span>
            <span className="inline-flex items-center gap-1.5 text-brand-primary font-semibold">
              Trust score: {c.trust_score}/100
            </span>
          </div>

          <div className="mt-8 premium-card p-7">
            <h2 className="font-serif-display text-2xl mb-3 text-brand-text">The story</h2>
            <p className="text-brand-text/85 leading-relaxed whitespace-pre-line" data-testid="campaign-story">{c.story}</p>
          </div>

          {c.proof_urls?.length > 0 && (
            <div className="mt-8 premium-card p-7">
              <h2 className="font-serif-display text-2xl mb-4 text-brand-text">Proof & documents</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {c.proof_urls.map((url, i) => (
                  <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer"
                     className="aspect-square rounded-2xl border border-brand bg-brand-bg overflow-hidden hover:border-brand-primary transition-colors flex items-center justify-center group">
                    {/\.(jpg|jpeg|png|webp|gif)$/i.test(url) ? (
                      <img src={url} alt={`Proof ${i + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-brand-muted group-hover:text-brand-primary">
                        <FileText size={28} strokeWidth={1.4} />
                        <span className="text-xs">Open file</span>
                        <ExternalLink size={12} />
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {data.recent_donations?.length > 0 && (
            <div className="mt-8 premium-card p-7">
              <h2 className="font-serif-display text-2xl mb-4 text-brand-text">Recent supporters</h2>
              <ul className="space-y-3">
                {data.recent_donations.slice(0, 8).map((d) => (
                  <li key={d.id} className="flex items-start gap-3 py-2 border-b border-brand last:border-0">
                    <div className="w-9 h-9 rounded-full bg-brand-pink flex items-center justify-center">
                      <Heart size={15} strokeWidth={1.6} className="text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-brand-text">{d.anonymous ? "Anonymous" : d.donor_name}</p>
                      {d.message && <p className="text-sm text-brand-muted mt-0.5">"{d.message}"</p>}
                    </div>
                    <span className="font-serif-display text-brand-primary text-lg whitespace-nowrap">{formatGBP(d.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        {/* Right: Sticky Donation */}
        <motion.aside initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-1">
          <div className="lg:sticky lg:top-[100px] space-y-5">
            <div className="premium-card p-7" data-testid="donation-box">
              <p className="font-serif-display text-4xl text-brand-text">{formatGBP(c.amount_raised || 0)}</p>
              <p className="text-sm text-brand-muted mt-1">raised of {formatGBP(c.amount_needed)} goal</p>

              <div className="mt-4">
                <div className="w-full h-2 bg-brand-pink rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #E91E63 0%, #FF4F8A 50%, #FFB72E 100%)" }} data-testid="detail-progress" />
                </div>
                <p className="text-xs text-brand-muted mt-2">{pct}% funded • {c.donor_count || 0} donors</p>
              </div>

              <div className="divider-line my-6" />

              <p className="eyebrow mb-3">Donate (GBP)</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {PRESET_AMOUNTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(p)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all
                      ${amount === p
                        ? "bg-brand-primary text-white border-brand-primary"
                        : "bg-brand-bg text-brand-text border-brand hover:border-brand-primary"}`}
                    data-testid={`donate-preset-${p}`}
                  >
                    £{p}
                  </button>
                ))}
              </div>

              <input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input-premium text-2xl font-serif-display text-center"
                placeholder="Custom amount"
                data-testid="donation-amount-input"
              />

              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                className="input-premium mt-3"
                placeholder="Your name (optional)"
                data-testid="donor-name-input"
              />

              {!user && (
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="input-premium mt-3"
                  placeholder="Email for receipt (optional)"
                  data-testid="donor-email-input"
                />
              )}

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input-premium mt-3 !min-h-[80px]"
                placeholder="Leave a message of hope (optional)"
                data-testid="donor-message-input"
              />

              <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm text-brand-text">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  className="rounded"
                  data-testid="anonymous-checkbox"
                />
                Donate anonymously
              </label>

              <button
                onClick={handleDonate}
                disabled={submitting}
                className="btn-gold w-full mt-5 !py-3.5 text-base"
                data-testid="donate-now-btn"
              >
                <Heart size={16} strokeWidth={1.8} className="mr-2" />
                {submitting ? "Redirecting…" : `Donate ${formatGBP(amount || 0)}`}
              </button>
              <p className="text-[0.7rem] text-brand-muted text-center mt-3">
                Secure payment via Stripe
              </p>
            </div>

            <button
              onClick={() => setShowReport(!showReport)}
              className="w-full text-xs text-brand-muted hover:text-brand-primary inline-flex items-center justify-center gap-1.5"
              data-testid="report-toggle-btn"
            >
              <Flag size={12} strokeWidth={1.6} />
              Report this campaign
            </button>

            {showReport && (
              <div className="premium-card p-5">
                <p className="eyebrow mb-3 inline-flex items-center gap-1.5">
                  <ShieldAlert size={12} strokeWidth={1.8} /> Tell us what's wrong
                </p>
                <textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="input-premium !min-h-[80px]"
                  placeholder="Describe the issue (min 8 chars)"
                  data-testid="report-reason-input"
                />
                <button onClick={handleReport} className="btn-pink w-full mt-3 !py-2.5 text-sm" data-testid="report-submit-btn">
                  Submit report
                </button>
              </div>
            )}
          </div>
        </motion.aside>
      </div>
    </div>
  );
}
